import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import RefreshOverlay from 'Components/DesignSystem/Table/RefreshOverlay';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import { ArrowDown, ArrowRight, Sort, Hashtag } from 'Components/DesignSystem/Iconography/Icons';

// Local Components
import { ActionBar, ExpandedRowContent, TablePaginationBar } from '../../components';

// API Services
import { getDataReviewProducts } from 'api/DataReview';

// Utilities
import { transformToProcessedProduct, transformPagination, PaginationInfo } from '../../utils';

// Types
import { ProcessedProduct } from '../../types';

// View props interface
interface ProcessedViewProps {
  channelKey: string;
  onRefresh?: () => void;
}

// Internal row state type
interface ProcessedRowState {
  product: ProcessedProduct;
  isSelected: boolean;
  isExpanded: boolean;
}

// Default pagination values
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

// Sort configuration
type SortField = 'updated_at' | 'name' | 'category' | 'total_new_count' | 'total_updates_count';
type SortOrder = 'asc' | 'desc';

const DEFAULT_SORT_BY: SortField = 'updated_at';
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

/**
 * ProcessedView Component
 *
 * Displays successfully processed imports using SINGLE UNIFIED TABLE
 * Columns: Checkbox | # | Product | Category | New Product | Processed By | Processed On | Expand
 *
 * Key features:
 * - NO Row Action dropdown in ActionBar (just Search + Filter)
 * - NO Edit button in ActionBar (showEditButton=false)
 * - "New Product" shows colored text (Yes=green-success-500, No=grey-600), NOT Badge
 * - Has Processed By and Processed On columns
 * - Supports row expansion with IgnoredExpandedContent
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11680-17254
 */
const ProcessedView: React.FC<ProcessedViewProps> = ({ channelKey, onRefresh }) => {
  // State
  const [searchValue, setSearchValue] = useState('');
  // Debounced mirror of searchValue. The data fetch keys off this (not the raw
  // value) so typing fires ONE request after 300ms instead of one per keystroke
  // (search=c, search=co, search=col, ...). The input stays bound to searchValue
  // for instant feedback. Mirrors the debounce already used in MissingMappingView.
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchValue(searchValue.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchValue]);
  const [processedRows, setProcessedRows] = useState<ProcessedRowState[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
    totalPages: 0,
  });

  // Computed values
  const selectedCount = processedRows.filter((r) => r.isSelected).length;

  // Tracks whether the component is still mounted. Guards setState calls
  // inside async fetchProducts so a fetch that resolves after tab-switch
  // doesn't fire a "state update on unmounted component" warning.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch products from API
   */
  const fetchProducts = useCallback(async () => {
    if (!channelKey) return;

    setIsLoading(true);
    try {
      const response = await getDataReviewProducts(channelKey, {
        tab: 'processed',
        page: pagination.page,
        per_page: pagination.perPage,
        search: debouncedSearchValue || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (!isMountedRef.current) return;

      if (response.done && response.data) {
        // Transform API data to frontend format
        const transformedRows = response.data.map((product) => ({
          product: transformToProcessedProduct(product),
          isSelected: false,
          isExpanded: false,
        }));
        setProcessedRows(transformedRows);
        setPagination(transformPagination(response.page));
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch processed products:', error);
      toast.error('Failed to load processed products');
      setProcessedRows([]);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [channelKey, pagination.page, pagination.perPage, debouncedSearchValue, sortBy, sortOrder]);

  // Fetch products when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Handlers
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleFilterClick = () => {
    // TODO: Implement filter functionality
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const renderSortIcon = (field: SortField) => {
    if (sortBy === field) {
      return (
        <span className={`tw-text-accent ${sortOrder === 'asc' ? 'tw-rotate-180' : ''} tw-inline-block tw-transition-transform`}>
          <ArrowDown />
        </span>
      );
    }
    return <span className="tw-text-ink-tertiary"><Sort /></span>;
  };

  const renderMetadataField = (label: string, value: string) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-text-xs tw-font-normal tw-text-ink-secondary">{label}</span>
      <span className="tw-text-xs tw-font-semibold tw-text-ink">{value || '-'}</span>
    </div>
  );

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setProcessedRows((prev) => prev.map((row) => ({ ...row, isSelected: checked })));
  };

  const handleRowSelect = (index: number, selected: boolean) => {
    setProcessedRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isSelected: selected };
      setSelectAll(updated.every((r) => r.isSelected));
      return updated;
    });
  };

  const handleRowExpand = (index: number) => {
    setProcessedRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isExpanded: !updated[index].isExpanded };
      return updated;
    });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPerPage: string | number) => {
    setPagination((prev) => ({ ...prev, perPage: newPerPage as number, page: DEFAULT_PAGE }));
  };

  return (
    <div className="tw-space-y-4">
      {/* Action Bar - NO bulkActions prop (no Row Action dropdown), NO Edit button */}
      <ActionBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        /* § 288 — the table fetch, plus the 300ms debounce in front of it. `.trim()`
           on the left because that is what the debounce commits: comparing the raw
           value would leave the spinner running forever on a trailing space. */
        isSearching={isLoading || searchValue.trim() !== debouncedSearchValue}
        onFilterClick={handleFilterClick}
        showEditButton={false}
        selectedCount={selectedCount}
      />

      {/* Single Unified Table */}
      {(
        <div className="tw-relative tw-border tw-border-edge tw-rounded tw-flex tw-flex-col" style={{ maxHeight: 'calc(100vh - 280px)', minHeight: '400px' }}>
          {/*
            The "Refreshing" chip Guide/ISR shows on the same gesture. These
            tables are hand-rolled rather than the DS `Table`, so they got the
            dim without the chip — the rows faded and nothing said why. Shares
            `Table`'s implementation rather than restating it.

            `rows.length > 0` is the whole condition: a COLD load has nothing to
            dim and shows its spinner row instead, and drawing both would put
            "Refreshing" on top of "Loading...".
          */}
          <RefreshOverlay
            active={isLoading && processedRows.length > 0}
            data-testid="catalog-table-refreshing-processed"
          />
          {/* Header — does not scroll. flex-shrink-0 pins it at natural height;
              overflow:hidden creates a scroll container so
              scrollbar-gutter:stable actually reserves right-side space,
              keeping column edges aligned with the body table below. */}
          <div className="tw-flex-shrink-0" style={{ overflow: 'hidden', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse">
            {/* Column Groups for Width Definitions */}
            {/* Checkbox | # | Product | Category | New Product | Processed By | Processed On | Expand */}
            <colgroup>
              <col style={{ width: '1%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '4%' }} />
            </colgroup>

            {/* Table Header - Two Rows */}
            <thead className="tw-bg-surface">
              {/* Section Header Row */}
              <tr className="tw-bg-surface">
                <th
                  colSpan={8}
                  className="tw-text-center tw-px-4 tw-py-3 tw-border-b tw-border-edge tw-bg-surface"
                >
                  <Typography variant="sub-title-label" className="tw-text-ink">
                    Imported Products
                  </Typography>
                </th>
              </tr>

              {/* Column Headers Row */}
              <tr className="tw-border-b tw-border-edge tw-bg-surface">
                {/* Spacer column (no checkboxes in Processed tab) */}
                <th className="tw-px-2 tw-py-2.5 tw-h-[44px] tw-bg-tone-neutral-tint" />
                {/* # column */}
                <th className="tw-px-2 tw-py-2.5 tw-h-[44px] tw-bg-tone-neutral-tint">
                  <div className="tw-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                    <Hashtag />
                  </div>
                </th>
                {/* Product column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('name')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Product
                    </Typography>
                    {renderSortIcon('name')}
                  </span>
                </th>
                {/* Category column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('category')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Category
                    </Typography>
                    {renderSortIcon('category')}
                  </span>
                </th>
                {/* New Product column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      New Product
                    </Typography>
                  </span>
                </th>
                {/* Processed By column */}
                <th className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint">
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Processed By
                    </Typography>
                  </span>
                </th>
                {/* Processed On column (sortable) */}
                <th
                  className="tw-px-3 tw-py-2.5 tw-text-left tw-h-[44px] tw-bg-tone-neutral-tint tw-cursor-pointer hover:tw-bg-surface-sunken"
                  onClick={() => handleSort('updated_at')}
                >
                  <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Typography variant="title-header" className="tw-text-ink-secondary">
                      Processed On
                    </Typography>
                    {renderSortIcon('updated_at')}
                  </span>
                </th>
                {/* Expand column - no header icon */}
                <th className="tw-px-2 tw-py-2.5 tw-text-center tw-h-[44px] tw-bg-tone-neutral-tint">
                </th>
              </tr>
            </thead>
          </table>
          </div>

          {/* Body — independent scroll container with the SAME colgroup as the
              header. scrollbar-gutter:stable on both sides ensures columns
              stay aligned regardless of whether the vertical scrollbar
              is visible. */}
          <div className="tw-flex-1" style={{ overflow: 'auto', scrollbarGutter: 'stable' }}>
          <table className="tw-w-full tw-border-collapse tw-table-fixed">
            <colgroup>
              <col style={{ width: '1%' }} />
              <col style={{ width: '3%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '4%' }} />
            </colgroup>

            {/* Table Body */}
            <tbody
              /*
                Rows stay mounted through a refetch and DIM, instead of being swapped
                for a spinner row (§ Guide/ISR parity, 2026-08-31). Swapping emptied
                the table body on every page click, so the page height collapsed and
                snapped back and the row under the pointer moved — the change read as
                a reload rather than a page turn.

                `pointer-events-none` matters as much as the opacity: without it the
                stale rows stay clickable, and expanding row 3 of the OLD page while
                the new one lands is a real way to act on data that is no longer there.

                The spinner still shows on a COLD load, where there is nothing to dim.
              */
              aria-busy={isLoading || undefined}
              className={`tw-transition-opacity ${isLoading && processedRows.length > 0 ? 'tw-opacity-40 tw-pointer-events-none' : ''}`}
            >
              {/* Loading State */}
              {/* Cold load only — a refetch dims the rows above instead. */}
              {isLoading && processedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="tw-px-4 tw-py-12 tw-text-center">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-[3px]">
                      <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-edge tw-border-t-accent" />
                      <Typography variant="body-primary" className="tw-text-ink-secondary">Loading...</Typography>
                    </div>
                  </td>
                </tr>
              )}
              {processedRows.map((row, index) => {
                const isEvenRow = index % 2 === 1;
                const cellBgClass = row.isSelected
                  ? ''
                  : `${isEvenRow ? 'tw-bg-surface-sunken' : 'tw-bg-surface'} group-hover:tw-bg-tone-neutral-tint`;

                return (
                  <React.Fragment key={row.product.id}>
                    {/* Main Row */}
                    <tr
                      className={`tw-group tw-border-b tw-border-edge tw-h-[40px] ${row.isSelected ? 'tw-bg-accent-tint' : 'hover:tw-bg-tone-neutral-tint'}`}
                    >
                      {/* Spacer (no checkboxes in Processed tab) */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`} />
                      {/* Row number */}
                      <td className={`tw-px-2 tw-py-2 ${cellBgClass}`}>
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <Typography variant="title-header" className="tw-text-ink">
                            {(pagination.page - 1) * pagination.perPage + index + 1}
                          </Typography>
                        </div>
                      </td>
                      {/* Product name */}
                      <td className={`tw-pl-6 tw-pr-3 tw-py-2 ${cellBgClass}`} title={row.product.name}>
                        <Typography variant="title-header" className="tw-text-ink">
                          {row.product.name.length > 21
                            ? `${row.product.name.slice(0, 21)}…`
                            : row.product.name}
                        </Typography>
                      </td>
                      {/* Category — `tw-truncate` needs a block-level container
                          with a constrained width. Typography renders inline,
                          so wrap it in a div and put `tw-overflow-hidden` on
                          the td so the ellipsis actually clips. */}
                      <td className={`tw-px-3 tw-py-2 tw-overflow-hidden ${cellBgClass}`}>
                        <div className="tw-truncate" title={row.product.category}>
                          <Typography variant="title-header" className="tw-text-ink tw-truncate">
                            {(row.product.category || '')}
                          </Typography>
                        </div>
                      </td>
                      {/* New Product - colored text (Yes=green, No=grey) */}
                      <td className={`tw-px-3 tw-py-2 ${cellBgClass}`}>
                        <Typography
                          variant="title-header"
                          className={row.product.isNewProduct ? 'tw-text-tone-success-glyph' : 'tw-text-ink-secondary'}
                        >
                          {row.product.isNewProduct ? 'Yes' : 'No'}
                        </Typography>
                      </td>
                      {/* Processed By */}
                      <td className={`tw-px-3 tw-py-2 ${cellBgClass}`}>
                        <Typography variant="title-header" className="tw-text-ink tw-truncate">
                          {(row.product.processedBy || '')}
                        </Typography>
                      </td>
                      {/* Processed On */}
                      <td className={`tw-px-3 tw-py-2 ${cellBgClass}`}>
                        <Typography variant="title-header" className="tw-text-ink tw-truncate">
                          {(row.product.processedOn || '')}
                        </Typography>
                      </td>
                      {/* Expand */}
                      <td className={`tw-px-2 tw-py-2 tw-text-center ${cellBgClass}`}>
                        <button data-testid={`processedview-processedview-button-1-${index}`}
                          onClick={() => handleRowExpand(index)}
                          className="tw-p-1 tw-rounded hover:tw-bg-surface-hover-strong tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
                        >
                          {row.isExpanded ? <ArrowDown /> : <ArrowRight />}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Row Content */}
                    {row.isExpanded && (
                      <>
                        <ExpandedRowContent
                          importedAttributes={row.product.attributes || []}
                          catalogAttributes={[]}
                          isEditMode={false}
                          singleSection={true}
                          productName={row.product.name}
                          // Match the outer colgroup so the # column in the
                          // expanded attribute rows aligns with the outer #.
                          colWidths={['1%', '3%', '24%', '21%', '13%', '20%', '14%', '4%']}
                        />
                        <tr className="tw-bg-surface tw-border-t tw-border-edge">
                          <td colSpan={8} className="tw-px-3 tw-py-2">
                            <div className="tw-mb-2">
                              {renderMetadataField('Description', row.product.description || '')}
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-8 tw-mb-2">
                              {renderMetadataField('SKU', row.product.sku || '')}
                              {renderMetadataField('Price', row.product.price || '')}
                              {renderMetadataField('UPC', row.product.upc || '')}
                              {renderMetadataField('Model Number', row.product.modelNumber || '')}
                            </div>
                            <div className="tw-flex tw-items-center tw-gap-8">
                              {renderMetadataField('PLS', row.product.pls || '')}
                              {renderMetadataField('Parent ID', row.product.parentId || '')}
                              {renderMetadataField('Version', row.product.version || '')}
                              {renderMetadataField('Real SKU', row.product.realSku || '')}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {!isLoading && processedRows.length === 0 && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                No processed products found
              </Typography>
            </div>
          )}
          </div>

          {/*
             The guard no longer includes `!isLoading`. It used to, and the strip therefore
             UNMOUNTED on every page click: the button vanished from under the pointer,
             keyboard focus fell to <body>, and the row it occupied collapsed so the page
             jumped. It stays mounted and takes `disabled` instead — which is what Guide/ISR's
             equivalent does.
          
             What remains here is `total > perPage`, and it stays at the CALL SITE: whether a
             table needs a pager at all is the view's question, not the bar's.
          */}
          {pagination.total > pagination.perPage && (
            <TablePaginationBar
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              disabled={isLoading}
              data-testid="catalog-pagination-processed"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProcessedView;
